/**
 * One job row of the transfers popover: destination file name, state, bytes,
 * `attempt` when > 1, the inline row notice, and the controls the snapshot
 * allows (Cancel by state; Resume / Restart by `recovery` only; Show in folder
 * on a completed local file). Disabled buttons carry the reason as `title`; a
 * stopped restart-only job shows Resume unavailable with main's `recoveryReason`
 * (integration review F2, decision P3) instead of hiding it.
 */
import { Button } from '../../../components/ui/button';
import { ReasonButton } from '../../../components/ui/ReasonButton';
import { Progress } from '../../../components/ui/progress';
import { ArrowDownToLine, ArrowUpFromLine, Copy, ShieldCheck, Trash2 } from 'lucide-react';
import type { TransferJob } from '../../../types/azureStorageTransfers';
import { aclJobSummary } from '../../../core/engines/azureStorageAclJobText';
import { canCancelTransfer, formatTransferBytes, isActiveTransferState, recoveryOffersFor, transferExportCompletionNote, transferFileName, transferLocalPath, transferProgressPercent, transferRemoteCleanupLine, transferRowFailure, transferStateLabel } from '../model/transferJobRules';
import type { TransferPendingAction } from '../store/transfersStore';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { transferItemsText, transferLocationLine, transferOperationLabel, transferRoute } from '../model/transferRowText';
import { formatStorageDateTime, storageDateTimeUtc } from '../../workspace/storage-explorer/utils/storageDateTime';
import { safeConnectionName } from '../../../types/safeConnectionDisplay';

export type TransferJobRowProps = {
  job: TransferJob;
  pending?: TransferPendingAction;
  notice?: string;
  lastErrorMessage?: string;
  throughputBps?: number;
  canReveal: boolean;
  onCancel: (jobId: string) => void;
  onResume: (jobId: string) => void;
  onRestart: (jobId: string) => void;
  onOpenFolder: (jobId: string, path: string) => void;
  /** S7 review D F4: offered when the job's connection can never remove its staged files (`transferCleanupOffer`). */
  onForget?: (jobId: string) => void;
  /** Review E D-2: the connection is only disconnected — connecting, then Clear finished, removes the staged files. */
  connectToCleanUp?: boolean;
  /** C1 (N17): why Resume / Restart cannot run — the job's connection was removed, or is read-only for a write. */
  recoveryRefusal?: string;
};

const PENDING_REASON: Record<TransferPendingAction, string> = { resume: 'Resume requested', restart: 'Restart requested', cancel: 'Cancel requested' };

const KIND_ICONS = { download: ArrowDownToLine, upload: ArrowUpFromLine, copy: Copy, delete: Trash2, acl: ShieldCheck, export: ArrowDownToLine } as const;

export function TransferJobRow({ job, pending, notice, lastErrorMessage, throughputBps, canReveal, onCancel, onResume, onRestart, onOpenFolder, onForget, connectToCleanUp, recoveryRefusal }: TransferJobRowProps) {
  // A13 / U14: the operation + target title, the connection alias + remote path line, source → destination on hover / focus.
  const connections = useConnectionStore((state) => state.connections);
  const aliasOf = (connectionId: string): string => { const found = connections.find((connection) => connection.id === connectionId); return safeConnectionName(found, 'Removed connection'); };
  const route = transferRoute(job, aliasOf);
  const cancel = canCancelTransfer(job);
  const recovery = recoveryOffersFor(job);
  const percent = transferProgressPercent(job);
  const localPath = transferLocalPath(job);
  const pendingReason = pending ? PENDING_REASON[pending] : undefined;
  const bytesValue = job.bytesTotal !== undefined ? `${formatTransferBytes(job.bytesDone)} / ${formatTransferBytes(job.bytesTotal)}` : formatTransferBytes(job.bytesDone);
  const bytes = job.kind === 'export' ? `${job.itemsDone} entities · ${bytesValue}` : bytesValue;
  const failure = transferRowFailure(job, lastErrorMessage);
  const isAcl = job.kind === 'acl';
  const Icon = KIND_ICONS[job.kind];
  const completionNote = transferExportCompletionNote(job);
  const remoteCleanup = transferRemoteCleanupLine(job);

  return (
    // F5 (last UI/UX pass): the divider belongs to the job's whole block in `TransfersPanel` (row, typed confirmation,
    // errors) — on the row it sat between the row and its own "Show N errors".
    <div className="flex flex-col gap-1" data-testid={`transfer-row-${job.id}`} data-state={job.state} data-recovery={job.recovery}>
      <div className="flex items-center gap-2 min-w-0" title={route} tabIndex={0} aria-label={`${transferOperationLabel(job.kind, job.acl)} ${transferFileName(job)}: ${route}`} data-testid={`transfer-row-title-${job.id}`}>
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="shrink-0 text-sm text-muted-foreground" data-testid={`transfer-row-operation-${job.id}`}>{transferOperationLabel(job.kind, job.acl)}</span>
        <span className="truncate text-sm font-semibold flex-1" data-testid={`transfer-row-name-${job.id}`}>{transferFileName(job)}</span>
        <span className="text-xs text-muted-foreground shrink-0" title={storageDateTimeUtc(job.startedAt)} data-testid={`transfer-row-started-${job.id}`}>{formatStorageDateTime(job.startedAt)}</span>
        <span className="text-xs text-muted-foreground shrink-0" data-testid={`transfer-row-state-${job.id}`}>{transferStateLabel(job.state, job.kind)}</span>
      </div>
      <div className="truncate pl-6 text-xs text-muted-foreground" title={route} data-testid={`transfer-row-location-${job.id}`}>{transferLocationLine(job, aliasOf)}</div>
      {isActiveTransferState(job.state) && <Progress value={percent} className="h-1" data-testid={`transfer-row-progress-${job.id}`} data-indeterminate={percent === undefined ? 'true' : 'false'} />}
      <div className="flex items-center gap-2 text-xs text-muted-foreground tabular-nums">
        {!isAcl && <span data-testid={`transfer-row-bytes-${job.id}`}>{bytes}</span>}
        {job.itemsTotal !== undefined && job.kind !== 'export' && !isAcl && <span data-testid={`transfer-row-items-${job.id}`}>{transferItemsText(job.itemsDone, job.itemsTotal)}</span>}
        {/* C1 (N26): a speed only while the job runs — a cancelled one kept its last "21 MB/s". */}
        {isActiveTransferState(job.state) && throughputBps !== undefined && throughputBps > 0 && <span data-testid={`transfer-row-throughput-${job.id}`}>{formatTransferBytes(throughputBps)}/s</span>}
        {job.attempt > 1 && <span data-testid={`transfer-row-attempt-${job.id}`}>attempt {job.attempt}</span>}
        {notice && <span className="text-warning" data-testid={`transfer-row-notice-${job.id}`}>{notice}</span>}
      </div>
      {isAcl && <div className="text-xs text-muted-foreground break-words" data-testid={`transfer-row-acl-summary-${job.id}`}>{aclJobSummary(job)}</div>}
      {failure && <div className="text-xs text-foreground break-words" data-testid={`transfer-row-error-${job.id}`}>{failure}</div>}
      {completionNote && <div className="text-xs text-muted-foreground" data-testid={`transfer-row-export-note-${job.id}`}>{completionNote}</div>}
      {remoteCleanup && <div className="text-xs text-muted-foreground break-words" data-testid={`transfer-row-remote-cleanup-${job.id}`}>{remoteCleanup}</div>}
      {connectToCleanUp && <div className="text-xs text-muted-foreground" data-testid={`transfer-connect-to-clean-${job.id}`}>Connect to clean up, then use Discard unfinished….</div>}
      <div className="flex items-center gap-1">
        {cancel.shown && (
          <Button variant="outline" size="xs" disabled={!cancel.enabled} title={cancel.reason} onClick={() => onCancel(job.id)} data-testid={`transfer-cancel-${job.id}`}>
            Cancel
          </Button>
        )}
        {recovery.resume && (
          <ReasonButton variant="outline" size="xs" side="right" reason={recoveryRefusal} disabled={Boolean(pending)} title={pendingReason} onClick={() => onResume(job.id)} data-testid={`transfer-resume-${job.id}`}>
            Resume
          </ReasonButton>
        )}
        {!recovery.resume && recovery.restart && job.recoveryReason && (
          <ReasonButton variant="outline" size="xs" side="right" reason={recoveryRefusal ?? job.recoveryReason} data-testid={`transfer-resume-${job.id}`}>Resume</ReasonButton>
        )}
        {recovery.restart && (
          <ReasonButton variant="outline" size="xs" side="right" reason={recoveryRefusal} disabled={Boolean(pending)} title={pendingReason} onClick={() => onRestart(job.id)} data-testid={`transfer-restart-${job.id}`}>
            Restart
          </ReasonButton>
        )}
        {onForget && (
          <Button variant="outline" size="xs" title="Remove this transfer from the list; its staged files stay where they are" onClick={() => onForget(job.id)} data-testid={`transfer-forget-${job.id}`}>
            Forget…
          </Button>
        )}
        {job.state === 'completed' && localPath && canReveal && (
          <Button variant="outline" size="xs" onClick={() => onOpenFolder(job.id, localPath)} data-testid={`transfer-open-folder-${job.id}`}>
            Show in folder
          </Button>
        )}
      </div>
    </div>
  );
}
